<template>
  <div class="verification-page">
    <!-- 主要内容 -->
    <div class="verification-content">
      <!-- 输入区域 -->
      <div class="input-section">
        <van-field
          v-model="securityCode"
          :placeholder="$t('verification.enterCode')"
          clearable
          maxlength="50"
          :error-message="errorMessage"
          class="code-input"
        >
          <template #button>
            <van-button
              size="small"
              type="primary"
              :loading="verifying"
              class="verify-btn"
              @click="handleManualVerification"
            >
              {{ $t('verification.verify') }}
            </van-button>
          </template>
        </van-field>
        <template v-if="!securityCode">
          <div class="scan-section">
            <van-button
              block
              type="primary"
              size="large"
              :loading="scanning"
              class="scan-btn"
              @click="handleDemoVerify"
            >
              {{ $t('verification.demoCode') }}
            </van-button>
          </div>

          <!-- 扫码提示 -->
          <div class="scan-tips">
            <van-icon name="info" />
            <span>{{ $t('verification.demoTip') }}</span>
          </div>
        </template>
      </div>

      <!-- 扫码区域 -->
      <div
        v-if="showScanner"
        class="scanner-section"
      >
        <div
          ref="scannerContainer"
          class="scanner-container"
        >
          <!-- 扫码界面将在这里动态创建 -->
        </div>

        <div class="scanner-overlay">
          <div class="scan-frame">
            <div class="frame-corner top-left" />
            <div class="frame-corner top-right" />
            <div class="frame-corner bottom-left" />
            <div class="frame-corner bottom-right" />
          </div>
          <p class="scan-guide">
            {{ $t('verification.scanGuide') }}
          </p>
        </div>

        <div class="scanner-actions">
          <van-button
            round
            type="default"
            icon="cross"
            class="close-scanner-btn"
            @click="stopScan"
          >
            {{ $t('common.close') }}
          </van-button>
        </div>
      </div>

      <!-- 查询结果 -->
      <div
        v-if="verificationResult"
        class="result-section"
      >
        <!-- 验证状态 -->
        <div
          class="status-card"
          :class="getStatusClass(verificationResult)"
        >
          <div class="status-icon">
            <van-icon :name="getStatusIcon(verificationResult)" />
          </div>
          <div class="status-content">
            <h3 class="status-title">
              {{ getStatusTitle(verificationResult) }}
            </h3>
            <p class="status-message">
              {{ verificationResult.message }}
            </p>
          </div>
        </div>
        <template v-if="verificationResult?.exists">
          <!-- 扫描信息 -->
          <div class="scan-info-card">
            <h4 class="card-title">
              {{ $t('verification.scanInfo') }}
            </h4>
            <div class="info-grid">
              <div class="info-item">
                <label>{{ $t('verification.isFirstScan') }}</label>
                <span :class="{ 'first-scan': verificationResult.isFirstScan }">
                  {{ verificationResult.isFirstScan ? $t('verification.yes') : $t('verification.no') }}
                </span>
              </div>
              <div class="info-item">
                <label>{{ $t('verification.scanTimes') }}</label>
                <span class="scan-count">{{ verificationResult.scanTimes }}</span>
              </div>
              <div
                v-if="verificationResult.firstScanTime"
                class="info-item"
              >
                <label>{{ $t('verification.firstScanTime') }}</label>
                <span class="scan-time">{{ formatDate(verificationResult.firstScanTime) }}</span>
              </div>
              <div class="info-item">
                <label>{{ $t('verification.currentScanTime') }}</label>
                <span class="scan-time">{{ formatDate(new Date()) }}</span>
              </div>
            </div>
          </div>

          <!-- 商品信息 -->
          <div
            v-if="verificationResult.commodityInfo"
            class="commodity-card"
          >
            <h4 class="card-title">
              {{ $t('verification.commodityInfo') }}
            </h4>
            <div class="commodity-content">
              <!--               商品图片-->
              <div
                v-if="verificationResult.commodityInfo.stylePhotoProxy"
                class="commodity-image"
              >
                <img
                  :src="verificationResult.commodityInfo.stylePhotoProxy"
                  :alt="verificationResult.commodityInfo.styleDescription"
                  @load="handleImageLoad"
                  @error="handleImageError"
                >
              </div>

              <!-- 商品详情 -->
              <div class="commodity-details">
                <h5 class="commodity-name">
                  {{ verificationResult.commodityInfo.styleDescription || $t('verification.unknownProduct') }}
                </h5>

                <div class="commodity-specs">
                  <div
                    v-if="verificationResult.commodityInfo.styleNo"
                    class="spec-item"
                  >
                    <label>{{ $t('verification.styleNo') }}</label>
                    <span>{{ verificationResult.commodityInfo.styleNo }}</span>
                  </div>
                  <div
                    v-if="verificationResult.commodityInfo.sptColor"
                    class="spec-item"
                  >
                    <label>{{ $t('verification.color') }}</label>
                    <span>{{ verificationResult.commodityInfo.sptColor }}</span>
                  </div>
                  <div
                    v-if="verificationResult.commodityInfo.sizeName"
                    class="spec-item"
                  >
                    <label>{{ $t('verification.size') }}</label>
                    <span>{{ verificationResult.commodityInfo.sizeName }}</span>
                  </div>
                  <div
                    v-if="verificationResult.commodityInfo.stockType"
                    class="spec-item"
                  >
                    <label>{{ $t('verification.category') }}</label>
                    <span>{{ verificationResult.commodityInfo.stockType }}</span>
                  </div>
                  <div
                    v-if="verificationResult.commodityInfo.country"
                    class="spec-item"
                  >
                    <label>{{ $t('verification.country') }}</label>
                    <span>{{ verificationResult.commodityInfo.country }}</span>
                  </div>
                  <div
                    v-if="verificationResult.commodityInfo.collectionNo"
                    class="spec-item"
                  >
                    <label>{{ $t('verification.collection') }}</label>
                    <span>{{ verificationResult.commodityInfo.collectionNo }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- 空状态 -->
      <div
        v-else-if="hasSearched"
        class="empty-state"
      >
        <van-empty
          :description="$t('verification.noResult')"
          image="search"
        >
          <van-button
            round
            type="primary"
            @click="resetSearch"
          >
            {{ $t('verification.tryAgain') }}
          </van-button>
        </van-empty>
      </div>
    </div>

    <!-- 加载遮罩 -->
    <van-overlay
      :show="verifying"
      class="verifying-overlay"
    >
      <div class="overlay-content">
        <van-loading
          size="24px"
          vertical
        >
          {{ $t('verification.verifying') }}
        </van-loading>
      </div>
    </van-overlay>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { showToast } from 'vant'
import { verifyProduct } from '@/api/verification'
import { getUserLang } from '@/storage/user'

const { t } = useI18n()
const router = useRouter()

// 响应式数据
const securityCode = ref('')
watch(() => securityCode.value, (code) => {
  if (code) {
    handleManualVerification(code)
  }
})
const errorMessage = ref('')
const verifying = ref(false)
const scanning = ref(false)
const showScanner = ref(false)
const hasSearched = ref(false)
const verificationResult = ref<any>(null)
const scannerContainer = ref<HTMLElement>()
let html5QrCode: any = null

// 设备类型检测
const deviceType = computed(() => {
  const width = window.innerWidth
  const height = window.innerHeight

  if (width >= 1200) {
    return 'pc'
  } else if (width >= 768) {
    return 'tablet'
  } else if (width >= 1080 && height >= 1920) {
    return 'large-mobile'
  } else {
    return 'mobile'
  }
})

// 方法
const validateSecurityCode = (code: string): boolean => {
  if (!code) {
    errorMessage.value = t('verification.codeRequired')
    return false
  }

  if (code.length < 8) {
    errorMessage.value = t('verification.codeTooShort')
    return false
  }

  errorMessage.value = ''
  return true
}

const handleManualVerification = async () => {
  if (!validateSecurityCode(securityCode.value)) {
    return
  }

  // const params = {
  //   code: securityCode.value
  // }
  verifyProduct(securityCode.value).then(res => {
    verificationResult.value = res
  })
}

const handleDemoVerify = () => {
  const lang = getUserLang()
  securityCode.value = lang === 'en-US' ? 'C428CE5E8BC245E38C97766E3B8FE801' : '5AD6C4EED48D4DF0A381B6DA7D0B926F'
}

const onScanSuccess = (decodedText: string) => {
  // 停止扫码
  stopScan()

  // 将扫码结果填入输入框
  securityCode.value = decodedText

  // 自动开始验证
  handleManualVerification()

  showToast(t('verification.scanSuccess'))
}

const onScanFailure = (error: string) => {
  // 扫码失败的处理，通常是继续扫描
  console.log('Scan failed:', error)
}

const stopScan = () => {
  if (html5QrCode) {
    html5QrCode.stop().then(() => {
      html5QrCode.clear()
      html5QrCode = null
    }).catch((error: any) => {
      console.error('Failed to stop scanner:', error)
    })
  }

  showScanner.value = false
  scanning.value = false
}

const getStatusClass = (result: any) => {
  if (result.isFirstScan) {
    return 'status-first-scan'
  } else if (result.scanTimes <= 2) {
    return 'status-normal'
  } else {
    return 'status-multiple-scans'
  }
}

const getStatusIcon = (result: any) => {
  if (result.isFirstScan) {
    return 'certificate'
  } else {
    return 'shield'
  }
}

const getStatusTitle = (result: any) => {
  if (result.isFirstScan) {
    return t('verification.authenticProduct')
  } else {
    return t('verification.verifiedProduct')
  }
}

const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString)
  const lang = getUserLang()
  return new Intl.DateTimeFormat(lang, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date)
}

const handleImageLoad = () => {
  // 图片加载成功
}

const handleImageError = () => {
  console.error('商品图片加载失败')
}

const resetSearch = () => {
  securityCode.value = ''
  errorMessage.value = ''
  verificationResult.value = null
  hasSearched.value = false
}

const route = useRoute()
// 生命周期
onMounted(() => {
  // 可以在这里添加初始化逻辑
  securityCode.value = route.query.code as string
})

onUnmounted(() => {
  // 清理扫码器
  if (html5QrCode) {
    stopScan()
  }
})
</script>

<style lang="scss" scoped>
.verification-page {
  min-height: 100vh;
  background-color: #f7f8fa;
}

.verification-content {
  padding: 16px;
  //padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);

  // PC端居中显示
  @media (min-width: 1200px) {
    //max-width: 600px;
    //margin: 0 auto;
    padding: 24px;
  }
}

/* 输入区域 */
.input-section {
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  .code-input {
    margin-bottom: 16px;

    :deep(.van-field__button) {
      padding-left: 12px;
    }

    // PC端调整输入框大小
    @media (min-width: 1200px) {
      :deep(.van-field__control) {
        font-size: 16px;
        padding: 12px 0;
      }
    }
  }

  .scan-section {
    margin-bottom: 12px;

    .scan-btn {
      height: 44px;
      border-radius: 22px;
      font-size: 16px;
      font-weight: 500;
    }
  }

  .scan-tips {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: #999;

    .van-icon {
      margin-right: 4px;
      font-size: 14px;
    }
  }

  // 验证按钮PC端优化
  .verify-btn {
    @media (min-width: 1200px) {
      min-width: 80px;
      height: 36px;
    }
  }
}

/* 扫码区域 */
.scanner-section {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: black;
  z-index: 1000;
  display: flex;
  flex-direction: column;

  .scanner-container {
    flex: 1;
    position: relative;
  }

  .scanner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;

    .scan-frame {
      width: 250px;
      height: 250px;
      position: relative;
      margin-bottom: 20px;

      .frame-corner {
        position: absolute;
        width: 20px;
        height: 20px;
        border: 2px solid #00ff00;

        &.top-left {
          top: 0;
          left: 0;
          border-right: none;
          border-bottom: none;
        }

        &.top-right {
          top: 0;
          right: 0;
          border-left: none;
          border-bottom: none;
        }

        &.bottom-left {
          bottom: 0;
          left: 0;
          border-right: none;
          border-top: none;
        }

        &.bottom-right {
          bottom: 0;
          right: 0;
          border-left: none;
          border-top: none;
        }
      }
    }

    .scan-guide {
      color: white;
      font-size: 14px;
      text-align: center;
      margin: 0;
    }
  }

  .scanner-actions {
    padding: 20px;
    text-align: center;

    .close-scanner-btn {
      background: rgba(255, 255, 255, 0.2);
      color: white;
      border: none;
    }
  }
}

/* 结果区域 */
.result-section {
  .status-card {
    background: white;
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

    &.status-first-scan {
      border-left: 4px solid #07c160;
    }

    &.status-normal {
      border-left: 4px solid #1989fa;
    }

    &.status-multiple-scans {
      border-left: 4px solid #ff976a;
    }

    .status-icon {
      .van-icon {
        font-size: 32px;

        .status-first-scan & {
          color: #07c160;
        }

        .status-normal & {
          color: #1989fa;
        }

        .status-multiple-scans & {
          color: #ff976a;
        }
      }
    }

    .status-content {
      flex: 1;

      .status-title {
        margin: 0 0 8px;
        font-size: 18px;
        font-weight: 600;
        color: #1a1a1a;
      }

      .status-message {
        margin: 0;
        font-size: 14px;
        color: #666;
        line-height: 1.4;
      }
    }
  }

  .scan-info-card,
  .commodity-card {
    background: white;
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

    .card-title {
      margin: 0 0 16px;
      font-size: 16px;
      font-weight: 600;
      color: #1a1a1a;
    }
  }

  .info-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: center;

      label {
        font-size: 14px;
        color: #666;
      }

      span {
        font-size: 14px;
        font-weight: 500;
        color: #1a1a1a;

        &.first-scan {
          color: #07c160;
          font-weight: 600;
        }

        &.scan-count {
          color: #1989fa;
        }

        &.scan-time {
          color: #999;
          font-size: 13px;
        }
      }
    }
  }

  .commodity-content {
    display: flex;
    gap: 16px;

    .commodity-image {
      width: 100px;
      height: 100px;
      border-radius: 8px;
      overflow: hidden;
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .commodity-details {
      flex: 1;
      min-width: 0;

      .commodity-name {
        margin: 0 0 12px;
        font-size: 16px;
        font-weight: 600;
        color: #1a1a1a;
        line-height: 1.3;
      }

      .commodity-specs {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .spec-item {
          display: flex;

          label {
            width: 80px;
            font-size: 12px;
            color: #999;
            flex-shrink: 0;
          }

          span {
            font-size: 12px;
            color: #333;
            flex: 1;
            text-align: left;
          }
        }
      }
    }
  }
}

.empty-state {
  margin-top: 60px;
}

.verifying-overlay {
  display: flex;
  align-items: center;
  justify-content: center;

  .overlay-content {
    text-align: center;
    color: white;
  }
}

/* 1080×1920 设备适配 */
@media (min-width: 1080px) and (min-height: 1920px) {
  .verification-content {
    padding: 32px;
    //max-width: 540px;
    //margin: 0 auto;
  }

  .input-section {
    padding: 32px;
    margin-bottom: 24px;

    .scan-section .scan-btn {
      height: 56px;
      border-radius: 28px;
      font-size: 18px;
    }

    .code-input {
      :deep(.van-field__control) {
        font-size: 18px;
        padding: 16px 0;
      }
    }
  }

  .scanner-overlay .scan-frame {
    width: 320px;
    height: 320px;
  }

  .result-section {
    .status-card,
    .scan-info-card,
    .commodity-card {
      padding: 28px;
      margin-bottom: 24px;
    }

    .status-card {
      .status-icon .van-icon {
        font-size: 40px;
      }

      .status-content .status-title {
        font-size: 22px;
      }

      .status-message {
        font-size: 16px;
      }
    }

    .info-grid .info-item {
      label, span {
        font-size: 16px;
      }
    }

    .commodity-content {
      gap: 24px;

      .commodity-image {
        width: 140px;
        height: 140px;
      }

      .commodity-name {
        font-size: 20px;
      }

      .commodity-specs .spec-item {
        label, span {
          font-size: 14px;
        }

        label {
          width: 100px;
        }
      }
    }
  }
}

/* PC端优化 */
@media (min-width: 1200px) {
  .verification-page {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
  }

  .verification-content {
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    width: 100%;
    //max-width: 600px;
    //margin: 40px auto;
  }

  .input-section {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

    .scan-section .scan-btn {
      height: 48px;
      border-radius: 24px;
      font-size: 16px;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(25, 137, 250, 0.3);
      }
    }
  }

  .result-section {
    .status-card,
    .scan-info-card,
    .commodity-card {
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      transition: transform 0.3s ease;

      &:hover {
        transform: translateY(-2px);
      }
    }

    .commodity-content {
      .commodity-image {
        width: 120px;
        height: 120px;
      }

      .commodity-name {
        font-size: 18px;
      }

      .commodity-specs .spec-item {
        label, span {
          font-size: 14px;
        }
      }
    }
  }
}

/* 平板设备 */
@media (max-width: 1199px) and (min-width: 768px) {
  .verification-content {
    //max-width: 500px;
    //margin: 0 auto;
    padding: 20px;
  }

  .input-section {
    padding: 24px;
  }

  .result-section {
    .commodity-content {
      .commodity-image {
        width: 120px;
        height: 120px;
      }
    }
  }
}

/* 移动端适配 */
@media (max-width: 767px) {
  .verification-content {
    padding: 12px;
  }

  .input-section {
    padding: 16px;
    margin-bottom: 12px;
  }

  .commodity-content {
    flex-direction: column;

    .commodity-image {
      width: 100%;
      height: 200px;
      margin-bottom: 12px;
    }
  }
}

/* 超小屏幕设备 */
@media (max-width: 480px) {
  .input-section {
    padding: 12px;
  }

  .result-section {
    .status-card,
    .scan-info-card,
    .commodity-card {
      padding: 16px;
    }

    .status-card {
      flex-direction: column;
      text-align: center;
      gap: 12px;
    }
  }
}

/* 横屏模式适配 */
@media (max-height: 500px) and (orientation: landscape) {
  .scanner-overlay .scan-frame {
    width: 200px;
    height: 200px;
  }

  .verification-content {
    padding: 8px;
  }

  .input-section {
    padding: 12px;
  }
}

/* 暗色模式支持 */
@media (prefers-color-scheme: dark) {
  .verification-page {
    background-color: #1a1a1a;
  }

  .input-section,
  .status-card,
  .scan-info-card,
  .commodity-card {
    background: #2a2a2a;
  }

  .status-content .status-title,
  .card-title,
  .commodity-name {
    color: #ffffff;
  }

  .status-message,
  .info-item label,
  .spec-item label {
    color: #cccccc;
  }

  .info-item span,
  .spec-item span {
    color: #ffffff;
  }

  // PC端暗色模式
  @media (min-width: 1200px) {
    .verification-page {
      background: linear-gradient(135deg, #1a1a1a 0%, #2d3748 100%);
    }

    .verification-content {
      background: #2a2a2a;
    }
  }
}

/* 高DPI屏幕优化 */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .input-section,
  .status-card,
  .scan-info-card,
  .commodity-card {
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1);
  }
}

/* 可访问性优化 */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* 大字体模式支持 */
@media (prefers-contrast: high) {
  .status-card {
    border-width: 2px !important;
  }

  .input-section {
    border: 1px solid #333;
  }
}

/* 触摸设备优化 */
@media (pointer: coarse) {
  .verify-btn,
  .scan-btn {
    min-height: 44px; // 苹果推荐的最小触摸目标尺寸
  }
}
</style>
